import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner, ErrorDisplay, EmptyState } from "@/components/loading";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X as XIcon,
} from "lucide-react";
import { VisualizationDialog } from "@/components/sensor-charts/visualization-dialog";
import { useSensors, useSensorSources, useSensorsCount, useParticipants } from "@/api/hooks";
import { buildDeviceLabelMapFromRecords } from "@/lib/device-labels";
import { formatDateShort, relativeTime } from "@/lib/format-date";
import type { SensorRecord } from "@/api/types";
import {
  formatValuesPreview,
  discoverValueColumns,
  formatCellValue,
  formatHealthConnectSummaryCell,
  getVisibleRecordValues,
  HEALTH_CONNECT_SUMMARY_COLUMNS,
  isoDaysAgo,
} from "./utils";
import { RecordSheet } from "./record-sheet";

const PAGE_LIMIT = 100;

interface SensorTableProps {
  participantId?: string;
  hideParticipantFilter?: boolean;
  hideParticipantColumn?: boolean;
  /** Show the Visualization button (participant detail page only). */
  showVisualization?: boolean;
  /** Override for device_id → label, e.g. hardware-grouped labels from participant-detail. */
  deviceLabels?: Record<string, string>;
}

export function SensorTable({
  participantId,
  hideParticipantFilter,
  hideParticipantColumn,
  showVisualization,
  deviceLabels: deviceLabelsOverride,
}: SensorTableProps) {
  const [sourceFilter, setSourceFilter] = useState("");
  const [internalParticipantFilter, setInternalParticipantFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selected, setSelected] = useState<SensorRecord | null>(null);
  const [vizOpen, setVizOpen] = useState(false);
  const [vizOpenedAt, setVizOpenedAt] = useState(0);

  const participantFilter = participantId ?? internalParticipantFilter;
  const setParticipantFilter = setInternalParticipantFilter;

  const resetPaging = () => {
    setCursorStack([undefined]);
    setPageIndex(0);
  };

  const onFilterChange = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    resetPaging();
  };

  const sources = useSensorSources();
  const participants = useParticipants();

  const activeCursor = cursorStack[pageIndex];

  const { data, isLoading, isFetching, isError, error, refetch } = useSensors({
    source: sourceFilter || undefined,
    user_id: participantFilter || undefined,
    device_id: deviceFilter || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
    limit: PAGE_LIMIT,
    cursor: activeCursor || undefined,
  });

  const totalCount = useSensorsCount({
    source: sourceFilter || undefined,
    user_id: participantFilter || undefined,
    device_id: deviceFilter || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
  });

  useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => {
      if (pageIndex === 0) refetch();
    }, 30_000);
    return () => window.clearInterval(id);
  }, [autoRefresh, pageIndex, refetch]);

  const records = useMemo(() => data?.data ?? [], [data]);
  const nextCursor = data?.next_cursor ?? null;

  const isTypedMode = Boolean(sourceFilter);
  const isHealthConnectMode = sourceFilter.toUpperCase() === "HEALTH_CONNECT";
  const valueColumns = useMemo(
    () =>
      isHealthConnectMode
        ? [...HEALTH_CONNECT_SUMMARY_COLUMNS]
        : isTypedMode
          ? discoverValueColumns(records)
          : [],
    [isHealthConnectMode, isTypedMode, records],
  );

  const recordDeviceLabels = useMemo(
    () => buildDeviceLabelMapFromRecords(records),
    [records],
  );

  const deviceLabels = useMemo(
    () => ({ ...recordDeviceLabels, ...(deviceLabelsOverride ?? {}) }),
    [recordDeviceLabels, deviceLabelsOverride],
  );

  const participantLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of participants.data?.data ?? []) {
      map[p.user_id] = p.username || p.user_id.slice(0, 8);
    }
    return map;
  }, [participants.data]);

  // Dedupe device dropdown by label so hardware-grouped devices appear once.
  // The option value is a comma-joined list of all device_ids sharing the label,
  // which the backend filter accepts as an `is_in` set.
  const deviceOptions = useMemo(() => {
    const byLabel = new Map<string, string[]>();
    const addId = (id: string) => {
      const label = deviceLabels[id] ?? id.slice(0, 10) + "…";
      const ids = byLabel.get(label) ?? [];
      if (!ids.includes(id)) ids.push(id);
      byLabel.set(label, ids);
    };
    if (deviceLabelsOverride) {
      for (const id of Object.keys(deviceLabelsOverride)) addId(id);
    }
    for (const r of records) {
      if (r.device_id) addId(r.device_id);
    }
    return Array.from(byLabel.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, ids]) => ({ label, value: ids.join(",") }));
  }, [records, deviceLabels, deviceLabelsOverride]);

  const renderParticipantValue = (val: unknown): ReactNode => {
    if (!val) return null;
    const s = String(val);
    return participantLabels[s] ?? s.slice(0, 8);
  };

  const renderDeviceValue = (val: unknown): ReactNode => {
    if (!val) return null;
    const firstId = String(val).split(",")[0];
    return deviceLabels[firstId] ?? firstId.slice(0, 10) + "…";
  };

  const hasActiveFilters =
    Boolean(sourceFilter) ||
    (!participantId && Boolean(participantFilter)) ||
    Boolean(deviceFilter) ||
    Boolean(fromDate) ||
    Boolean(toDate);

  const onResetFilters = () => {
    setSourceFilter("");
    if (!participantId) setParticipantFilter("");
    setDeviceFilter("");
    setFromDate("");
    setToDate("");
    resetPaging();
  };

  const onTimeChip = (kind: "24h" | "3d" | "7d") => {
    if (kind === "24h") setFromDate(isoDaysAgo(1));
    else if (kind === "3d") setFromDate(isoDaysAgo(3));
    else setFromDate(isoDaysAgo(7));
    setToDate("");
    resetPaging();
  };

  const onNextPage = () => {
    if (!nextCursor) return;
    setCursorStack((prev) => {
      const next = [...prev];
      next[pageIndex + 1] = nextCursor;
      return next;
    });
    setPageIndex((idx) => idx + 1);
  };

  const onPrevPage = () => {
    if (pageIndex === 0) return;
    setPageIndex((idx) => idx - 1);
  };

  const labelFor = (record: SensorRecord) =>
    participantLabels[record.participant_id] ?? record.participant_id.slice(0, 8);

  const deviceLabelFor = (record: SensorRecord) =>
    record.device_id ? (deviceLabels[record.device_id] ?? record.device_id.slice(0, 10) + "…") : "—";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Source</label>
          <Select
            value={sourceFilter}
            onValueChange={(val) => onFilterChange(setSourceFilter)(val === "__all__" ? "" : val ?? "")}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sources</SelectItem>
              {(sources.data?.data ?? []).map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!hideParticipantFilter && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Participant</label>
            <Select
              value={participantFilter}
              onValueChange={(val) =>
                onFilterChange(setParticipantFilter)(val === "__all__" ? "" : val ?? "")
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All participants">
                  {renderParticipantValue}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All participants</SelectItem>
                {(participants.data?.data ?? []).map((p) => (
                  <SelectItem key={p.user_id} value={p.user_id}>
                    {p.username || p.user_id.slice(0, 8)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Device</label>
          <Select
            value={deviceFilter}
            onValueChange={(val) => onFilterChange(setDeviceFilter)(val === "__all__" ? "" : val ?? "")}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All devices">
                {renderDeviceValue}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All devices</SelectItem>
              {deviceOptions.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">From</label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => onFilterChange(setFromDate)(e.target.value)}
            className="w-36"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">To</label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => onFilterChange(setToDate)(e.target.value)}
            className="w-36"
          />
        </div>

        <div className="flex flex-wrap gap-1">
          <Button variant="outline" size="sm" onClick={() => onTimeChip("24h")}>24h</Button>
          <Button variant="outline" size="sm" onClick={() => onTimeChip("3d")}>3d</Button>
          <Button variant="outline" size="sm" onClick={() => onTimeChip("7d")}>7d</Button>
        </div>

        {showVisualization && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setVizOpenedAt(Date.now());
              setVizOpen(true);
            }}
          >
            <BarChart3 className="size-3.5" />
            Visualization
          </Button>
        )}

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onResetFilters} className="text-muted-foreground">
            <XIcon className="size-3.5" />
            Reset
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-b py-2">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {isTypedMode && (
            <>
              <Badge variant="outline">{sourceFilter}</Badge>
              <span>{valueColumns.length} field{valueColumns.length !== 1 ? "s" : ""}</span>
              <span>·</span>
            </>
          )}
          <span>
            {totalCount.data != null && (
              <>
                <span className="font-medium text-foreground">
                  {totalCount.data.toLocaleString()}
                </span>{" "}
                found ·{" "}
              </>
            )}
            {records.length} loaded · page {pageIndex + 1}
          </span>
          {isFetching && !isLoading && (
            <span className="text-primary">refreshing…</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="size-3.5"
            />
            Auto-refresh
          </label>
          <Button variant="outline" size="sm" onClick={() => refetch()} title="Refresh">
            <RefreshCw className="size-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pageIndex === 0}
            onClick={onPrevPage}
          >
            <ChevronLeft className="size-3.5" />
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!nextCursor}
            onClick={onNextPage}
          >
            Next
            <ChevronRight className="size-3.5" />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <ErrorDisplay message={error.message} onRetry={() => refetch()} />
      ) : records.length === 0 ? (
        <EmptyState message="No sensor data matches your filters" />
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {!isTypedMode && <TableHead className="w-24">Source</TableHead>}
                <TableHead className="w-40">Collected</TableHead>
                {!hideParticipantColumn && <TableHead className="w-32">Participant</TableHead>}
                <TableHead className="w-24">Device</TableHead>
                {isTypedMode
                  ? valueColumns.map((col) => (
                      <TableHead key={col} className="font-mono text-xs">
                        {col}
                      </TableHead>
                    ))
                  : <TableHead>Values</TableHead>}
                <TableHead className="w-28">Uploaded</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record, i) => (
                <TableRow
                  key={i}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setSelected(record)}
                >
                  {!isTypedMode && (
                    <TableCell>
                      <Badge variant="outline">{record.source}</Badge>
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {formatDateShort(record.collected_at)}
                  </TableCell>
                  {!hideParticipantColumn && (
                    <TableCell className="font-mono text-xs">{labelFor(record)}</TableCell>
                  )}
                  <TableCell className="text-xs text-muted-foreground">
                    {deviceLabelFor(record)}
                  </TableCell>
                  {isTypedMode ? (
                    valueColumns.map((col) => (
                      <TableCell key={col} className="font-mono text-xs">
                        {isHealthConnectMode
                          ? formatHealthConnectSummaryCell(col, record.values?.[col])
                          : formatCellValue(record.source, col, record.values?.[col])}
                      </TableCell>
                    ))
                  ) : (
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {formatValuesPreview(getVisibleRecordValues(record))}
                    </TableCell>
                  )}
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {relativeTime(record.uploaded_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <RecordSheet
        record={selected}
        participantLabel={selected ? labelFor(selected) : ""}
        deviceLabel={selected ? deviceLabelFor(selected) : ""}
        onClose={() => setSelected(null)}
      />

      {showVisualization && (
        <VisualizationDialog
          open={vizOpen}
          onOpenChange={setVizOpen}
          openedAt={vizOpenedAt}
          filters={{
            source: sourceFilter || undefined,
            user_id: participantFilter || undefined,
            device_id: deviceFilter || undefined,
            from: fromDate || undefined,
            to: toDate || undefined,
          }}
        />
      )}
    </div>
  );
}
